/**
 * SuppliersPanel — cross-tender Supplier Console.
 * Browse/search the registry, ★ favorite, tag, add notes, see run history.
 * Recommended suppliers (auto from history + manual favorites) float to the top.
 * Profiles grow automatically when candidates are accepted in a tender Shortlist.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Star, Search, Plus, Award, Mail, MessageCircle, MapPin } from 'lucide-react';
import { forgeAPI } from '../../../services/api';

const card = {
  border: '1px solid var(--forge-border, #e3e7ec)', borderRadius: 10,
  background: 'var(--forge-surface, #fff)', padding: 12,
};
const grid = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 12 };
const inlineInput = {
  width: '100%', marginTop: 4, fontSize: 12, padding: '4px 6px',
  border: '1px solid var(--forge-border, #e3e7ec)', borderRadius: 6,
};

const StatChip = ({ label, value }) =>
  value == null || value === '' ? null : (
    <span className="forge-line-chip">{label}: {value}</span>
  );

const SuppliersPanel = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [query, setQuery] = useState('');
  const [favoriteOnly, setFavoriteOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ company: '', country: '', email: '', tags: '' });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await forgeAPI.listSuppliers(query, favoriteOnly);
      setSuppliers(data.suppliers || []);
    } catch (e) {
      /* keep prior list on error */
    }
    setLoading(false);
  }, [query, favoriteOnly]);

  useEffect(() => {
    const t = setTimeout(load, 250); // debounce search
    return () => clearTimeout(t);
  }, [load]);

  const replace = (updated) =>
    setSuppliers((prev) => prev.map((x) => (x.supplier_id === updated.supplier_id ? updated : x)));

  const toggleFav = async (s) => replace(await forgeAPI.favoriteSupplier(s.supplier_id, !s.favorite));
  const saveTags = async (s, str) =>
    replace(await forgeAPI.patchSupplier(s.supplier_id, {
      tags: str.split(',').map((t) => t.trim()).filter(Boolean),
    }));
  const saveNote = async (s, notes) =>
    replace(await forgeAPI.patchSupplier(s.supplier_id, { notes }));

  const addSupplier = async () => {
    if (!form.company) return;
    await forgeAPI.upsertSupplier({
      company: form.company,
      country: form.country,
      contacts: form.email ? { email: form.email } : undefined,
      tags: form.tags ? form.tags.split(',').map((t) => t.trim()).filter(Boolean) : [],
      source: 'manual',
      favorite: true,
    });
    setForm({ company: '', country: '', email: '', tags: '' });
    setAdding(false);
    load();
  };

  return (
    <div className="forge-shortlist">
      <div className="forge-spec-toolbar">
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flex: 1 }}>
          <Search size={14} />
          <input
            placeholder="Search suppliers, certs, tags…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            style={{ flex: 1, maxWidth: 340, ...inlineInput, marginTop: 0 }}
          />
          <label className="forge-muted" style={{ display: 'flex', gap: 4, alignItems: 'center', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={favoriteOnly} onChange={(e) => setFavoriteOnly(e.target.checked)} /> ★ only
          </label>
        </div>
        <button className="forge-btn-ghost" onClick={() => setAdding((a) => !a)}>
          <Plus size={14} /> Add supplier
        </button>
      </div>

      {adding && (
        <div className="forge-add-row">
          <input placeholder="Company" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} />
          <input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} />
          <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <input placeholder="Tags (comma sep)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <button className="forge-btn-sm" onClick={addSupplier}>Save</button>
        </div>
      )}

      <div className="forge-muted" style={{ margin: '6px 2px' }}>
        {suppliers.length} supplier{suppliers.length === 1 ? '' : 's'}{loading ? ' · loading…' : ''}
      </div>

      {suppliers.length === 0 && !loading && (
        <div className="forge-muted" style={{ padding: 16 }}>
          No saved suppliers yet. They’re added automatically when you accept candidates in a
          tender’s Shortlist, or add one manually above.
        </div>
      )}

      <div style={grid}>
        {suppliers.map((s) => (
          <div key={s.supplier_id} style={card}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
              <div>
                <div style={{ fontWeight: 600 }}>{s.company}</div>
                <div className="forge-muted" style={{ fontSize: 12 }}>
                  <MapPin size={11} /> {[s.city, s.country].filter(Boolean).join(', ') || '—'}
                </div>
              </div>
              <button
                title={s.favorite ? 'Unfavorite' : 'Favorite'}
                onClick={() => toggleFav(s)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }}
              >
                <Star size={18} fill={s.favorite ? '#f5b301' : 'none'} color={s.favorite ? '#f5b301' : '#9aa4b2'} />
              </button>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '6px 0' }}>
              {s.recommended && <span className="forge-rel forge-rel-high">★ Recommended</span>}
              {(s.certifications || []).map((c) => <span key={c} className="forge-line-chip">{c}</span>)}
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', margin: '4px 0' }}>
              <StatChip label="rating" value={s.stats?.rating} />
              <StatChip label="on-time%" value={s.stats?.on_time_rating} />
              <StatChip label="runs" value={s.run_count} />
              <StatChip label="awards" value={s.award_count} />
            </div>

            <div className="forge-muted" style={{ fontSize: 12, margin: '4px 0', display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {s.contacts?.email && <span><Mail size={11} /> {s.contacts.email}</span>}
              {s.contacts?.whatsapp && <span><MessageCircle size={11} /> {s.contacts.whatsapp}</span>}
            </div>

            <input
              style={inlineInput}
              defaultValue={(s.tags || []).join(', ')}
              placeholder="tags (comma separated)"
              onBlur={(e) => saveTags(s, e.target.value)}
            />
            <textarea
              style={{ ...inlineInput, resize: 'vertical' }}
              defaultValue={s.notes || ''}
              placeholder="Notes (info on hand)…"
              rows={2}
              onBlur={(e) => saveNote(s, e.target.value)}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default SuppliersPanel;
